// Virtual filesystem abstraction. The editor doesn't care *how* files got
// here — it calls `list()` and `read(path)`.
//
// Three implementations:
//   - DirectoryHandleSource: `showDirectoryPicker()` on Chromium/Edge.
//   - WebkitDirectorySource:  <input type="file" webkitdirectory> fallback.
//   - DragDropSource:         webkitGetAsEntry() on dropped folder.
//
// All three filter to .sas files and cache decoded text by path for repeat
// reads. Caches evict if total cached bytes exceeds CACHE_MAX.

export interface FileEntry {
  path: string;
  size: number;
}

export interface FolderSource {
  label: string;
  list(): Promise<FileEntry[]>;
  read(path: string): Promise<string>;
  /// Optional: presence signals write capability. Read-only sources
  /// (Webkit `<input webkitdirectory>`, drag-drop) deliberately omit
  /// it; callers gate via `typeof src.write === 'function'`.
  write?(path: string, text: string): Promise<void>;
  /// Optional: returns the directory handle if this source wraps one,
  /// for callers that need to verify FS Access permissions before
  /// writing or persist the handle to IndexedDB.
  rootHandle?(): FileSystemDirectoryHandle;
}

const CACHE_MAX = 256 * 1024 * 1024; // 256 MB

function matchesSas(name: string): boolean {
  return name.toLowerCase().endsWith('.sas');
}

abstract class BaseSource implements FolderSource {
  abstract label: string;
  protected cache = new Map<string, string>();
  protected cacheBytes = 0;

  abstract list(): Promise<FileEntry[]>;
  abstract read(path: string): Promise<string>;

  protected cacheStore(path: string, text: string): void {
    if (this.cacheBytes + text.length > CACHE_MAX) {
      // Simple LRU-ish: drop the oldest entry until we fit.
      for (const key of this.cache.keys()) {
        const old = this.cache.get(key)!;
        this.cache.delete(key);
        this.cacheBytes -= old.length;
        if (this.cacheBytes + text.length <= CACHE_MAX) break;
      }
    }
    this.cache.set(path, text);
    this.cacheBytes += text.length;
  }
}

// --- FileSystemDirectoryHandle (Chromium) ----------------------------------

export class DirectoryHandleSource extends BaseSource {
  label: string;
  constructor(private root: FileSystemDirectoryHandle) {
    super();
    this.label = root.name;
  }

  async list(): Promise<FileEntry[]> {
    const out: FileEntry[] = [];
    const stack: Array<{ handle: FileSystemDirectoryHandle; prefix: string }> = [
      { handle: this.root, prefix: '' },
    ];
    while (stack.length > 0) {
      const { handle, prefix } = stack.pop()!;
      // `entries()` is present on FileSystemDirectoryHandle (WICG spec).
      for await (const [name, child] of (handle as any).entries() as AsyncIterable<[string, FileSystemHandle]>) {
        const path = prefix ? `${prefix}/${name}` : name;
        if (child.kind === 'directory') {
          stack.push({ handle: child as FileSystemDirectoryHandle, prefix: path });
        } else if (matchesSas(name)) {
          const file = await (child as FileSystemFileHandle).getFile();
          out.push({ path, size: file.size });
        }
      }
    }
    out.sort((a, b) => a.path.localeCompare(b.path));
    return out;
  }

  async read(path: string): Promise<string> {
    const cached = this.cache.get(path);
    if (cached !== undefined) return cached;
    const parts = path.split('/');
    let dir: FileSystemDirectoryHandle = this.root;
    for (let i = 0; i < parts.length - 1; i += 1) {
      dir = await dir.getDirectoryHandle(parts[i]);
    }
    const fh = await dir.getFileHandle(parts[parts.length - 1]);
    const file = await fh.getFile();
    const text = await file.text();
    this.cacheStore(path, text);
    return text;
  }

  async write(path: string, text: string): Promise<void> {
    const parts = path.split('/');
    let dir: FileSystemDirectoryHandle = this.root;
    for (let i = 0; i < parts.length - 1; i += 1) {
      dir = await dir.getDirectoryHandle(parts[i], { create: true });
    }
    const fh = await dir.getFileHandle(parts[parts.length - 1], { create: true });
    // `createWritable()` defaults to keepExistingData:false → the swap
    // file is truncated on open and atomically replaced on close. One
    // `write` then `close` is the correct shape.
    const writable = await fh.createWritable();
    await writable.write(text);
    await writable.close();
    // Update the read cache so subsequent reads don't return the
    // pre-save bytes from the LRU.
    this.cache.delete(path);
    this.cacheStore(path, text);
  }

  rootHandle(): FileSystemDirectoryHandle {
    return this.root;
  }
}

// --- webkitdirectory fallback (Firefox) ------------------------------------

export class WebkitDirectorySource extends BaseSource {
  label: string;
  private files = new Map<string, File>();

  constructor(fileList: FileList) {
    super();
    this.label = 'picked folder';
    let rootPrefix = '';
    for (let i = 0; i < fileList.length; i += 1) {
      const f = fileList[i];
      const rel = (f as any).webkitRelativePath as string;
      if (!rootPrefix && rel.includes('/')) {
        rootPrefix = rel.split('/')[0];
      }
      if (matchesSas(f.name)) {
        // Strip the top-level folder name from the display path so both
        // implementations surface a consistent "relative to picked folder" key.
        const display = rel.startsWith(rootPrefix + '/')
          ? rel.slice(rootPrefix.length + 1)
          : rel;
        this.files.set(display, f);
      }
    }
    if (rootPrefix) this.label = rootPrefix;
  }

  async list(): Promise<FileEntry[]> {
    const out: FileEntry[] = [];
    for (const [path, f] of this.files) {
      out.push({ path, size: f.size });
    }
    out.sort((a, b) => a.path.localeCompare(b.path));
    return out;
  }

  async read(path: string): Promise<string> {
    const cached = this.cache.get(path);
    if (cached !== undefined) return cached;
    const f = this.files.get(path);
    if (!f) throw new Error(`file not found: ${path}`);
    const text = await f.text();
    this.cacheStore(path, text);
    return text;
  }
}

// --- drag-and-drop folder (any browser via webkitGetAsEntry) ---------------

export class DragDropSource extends BaseSource {
  label: string;
  private files = new Map<string, File>();

  private constructor(label: string, files: Map<string, File>) {
    super();
    this.label = label;
    this.files = files;
  }

  static async fromItems(items: DataTransferItemList): Promise<DragDropSource | null> {
    const entries: FileSystemEntry[] = [];
    for (let i = 0; i < items.length; i += 1) {
      const item = items[i];
      if (item.kind !== 'file') continue;
      const entry = (item as any).webkitGetAsEntry?.() as FileSystemEntry | null;
      if (entry) entries.push(entry);
    }
    if (entries.length === 0) return null;

    let label = 'dropped folder';
    const files = new Map<string, File>();

    async function walk(entry: FileSystemEntry, prefix: string): Promise<void> {
      if (entry.isDirectory) {
        if (!prefix && !label) label = entry.name;
        const dir = entry as FileSystemDirectoryEntry;
        const reader = dir.createReader();
        const batch = await new Promise<FileSystemEntry[]>((resolve, reject) => {
          const all: FileSystemEntry[] = [];
          const readBatch = () =>
            reader.readEntries((found) => {
              if (found.length === 0) resolve(all);
              else {
                all.push(...found);
                readBatch();
              }
            }, reject);
          readBatch();
        });
        for (const child of batch) {
          await walk(child, prefix ? `${prefix}/${child.name}` : child.name);
        }
      } else if (entry.isFile) {
        const fe = entry as FileSystemFileEntry;
        if (!matchesSas(fe.name)) return;
        const f = await new Promise<File>((resolve, reject) => fe.file(resolve, reject));
        files.set(prefix, f);
      }
    }

    const topLevelFolders = entries.filter((e) => e.isDirectory);
    if (topLevelFolders.length === 1) label = topLevelFolders[0].name;

    for (const entry of entries) {
      await walk(entry, entry.name);
    }

    // Normalise: if exactly one root folder, strip its name from paths.
    if (topLevelFolders.length === 1) {
      const root = topLevelFolders[0].name;
      const normalised = new Map<string, File>();
      for (const [p, f] of files) {
        const stripped = p.startsWith(root + '/') ? p.slice(root.length + 1) : p;
        normalised.set(stripped, f);
      }
      return new DragDropSource(label, normalised);
    }
    return new DragDropSource(label, files);
  }

  async list(): Promise<FileEntry[]> {
    const out: FileEntry[] = [];
    for (const [path, f] of this.files) {
      out.push({ path, size: f.size });
    }
    out.sort((a, b) => a.path.localeCompare(b.path));
    return out;
  }

  async read(path: string): Promise<string> {
    const cached = this.cache.get(path);
    if (cached !== undefined) return cached;
    const f = this.files.get(path);
    if (!f) throw new Error(`file not found: ${path}`);
    const text = await f.text();
    this.cacheStore(path, text);
    return text;
  }
}

// --- picker helpers --------------------------------------------------------

export async function pickFolder(): Promise<FolderSource | null> {
  if (typeof (window as any).showDirectoryPicker === 'function') {
    try {
      // `readwrite` so the permission grant covers both the file-tree
      // listing and any later Save action — one prompt instead of two.
      const handle = await (window as any).showDirectoryPicker({ mode: 'readwrite' });
      return new DirectoryHandleSource(handle);
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') return null;
      throw e;
    }
  }
  // Fallback: programmatically click an <input webkitdirectory>.
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    (input as any).webkitdirectory = true;
    input.multiple = true;
    input.style.display = 'none';
    document.body.appendChild(input);
    input.addEventListener('change', () => {
      const files = input.files;
      document.body.removeChild(input);
      resolve(files && files.length > 0 ? new WebkitDirectorySource(files) : null);
    });
    input.click();
  });
}

// --- IndexedDB handle persistence (Chromium FS Access only) ---------------
// `FileSystemDirectoryHandle` is structured-cloneable in Chromium, so we
// can stash the picked root in IndexedDB and re-attach on next session
// without re-prompting. Firefox/Safari throw `DataCloneError` — we guard
// at the call site by checking `showDirectoryPicker` support.

const IDB_NAME = 'kolilang';
const IDB_STORE = 'handles';
const IDB_KEY = 'rootHandle';
// Pre-rename DB name. Migrated once per session on first open so users
// who picked a folder before the rename don't have to re-pick it.
const LEGACY_IDB_NAME = 'sassy';

let legacyMigration: Promise<void> | null = null;

async function openIdb(): Promise<IDBDatabase> {
  const db = await new Promise<IDBDatabase>((resolve, reject) => {
    const req = indexedDB.open(IDB_NAME, 1);
    req.onupgradeneeded = () => {
      req.result.createObjectStore(IDB_STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  if (!legacyMigration) legacyMigration = migrateLegacyIdb(db);
  await legacyMigration;
  return db;
}

/// Copy the old `sassy` DB's store into the new DB (only when the new
/// one is still empty), then drop the old DB. Best-effort: any failure
/// resolves and the editor proceeds with the new empty DB — worst case
/// the user re-picks their folder once.
async function migrateLegacyIdb(db: IDBDatabase): Promise<void> {
  try {
    // `databases()` is missing in some browsers (notably older Firefox);
    // when absent we fall back to the abort-on-upgrade probe below.
    if (typeof indexedDB.databases === 'function') {
      const names = (await indexedDB.databases()).map((d) => d.name);
      if (!names.includes(LEGACY_IDB_NAME)) return;
    }
    const old = await new Promise<IDBDatabase | null>((resolve) => {
      const req = indexedDB.open(LEGACY_IDB_NAME);
      // If the DB doesn't exist, open() would create it — aborting the
      // versionchange transaction keeps the probe side-effect-free and
      // routes to onerror.
      req.onupgradeneeded = () => { req.transaction?.abort(); };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
    });
    if (!old) return;
    try {
      if (old.objectStoreNames.contains(IDB_STORE)) {
        const entries = await new Promise<{ keys: IDBValidKey[]; values: unknown[] }>(
          (resolve, reject) => {
            const tx = old.transaction(IDB_STORE, 'readonly');
            const store = tx.objectStore(IDB_STORE);
            const keysReq = store.getAllKeys();
            const valsReq = store.getAll();
            tx.oncomplete = () => resolve({ keys: keysReq.result, values: valsReq.result });
            tx.onerror = () => reject(tx.error);
          },
        );
        if (entries.keys.length > 0) {
          const newCount = await new Promise<number>((resolve, reject) => {
            const tx = db.transaction(IDB_STORE, 'readonly');
            const req = tx.objectStore(IDB_STORE).count();
            req.onsuccess = () => resolve(req.result);
            req.onerror = () => reject(req.error);
          });
          if (newCount === 0) {
            await new Promise<void>((resolve, reject) => {
              const tx = db.transaction(IDB_STORE, 'readwrite');
              const store = tx.objectStore(IDB_STORE);
              entries.keys.forEach((k, i) => store.put(entries.values[i], k));
              tx.oncomplete = () => resolve();
              tx.onerror = () => reject(tx.error);
            });
          }
        }
      }
    } finally {
      old.close();
    }
    indexedDB.deleteDatabase(LEGACY_IDB_NAME);
  } catch {
    // ignore — see doc comment.
  }
}

export async function saveRootHandle(handle: FileSystemDirectoryHandle): Promise<void> {
  if (typeof (window as any).showDirectoryPicker !== 'function') return;
  try {
    const db = await openIdb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, 'readwrite');
      tx.objectStore(IDB_STORE).put(handle, IDB_KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    db.close();
  } catch {
    // IDB write is best-effort — failure just means the next session
    // will need to re-pick. Don't block the active flow.
  }
}

export async function loadRootHandle(): Promise<FileSystemDirectoryHandle | null> {
  if (typeof (window as any).showDirectoryPicker !== 'function') return null;
  try {
    const db = await openIdb();
    const handle = await new Promise<FileSystemDirectoryHandle | null>((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, 'readonly');
      const req = tx.objectStore(IDB_STORE).get(IDB_KEY);
      req.onsuccess = () => resolve((req.result as FileSystemDirectoryHandle | undefined) ?? null);
      req.onerror = () => reject(req.error);
    });
    db.close();
    return handle;
  } catch {
    return null;
  }
}

export async function clearRootHandle(): Promise<void> {
  try {
    const db = await openIdb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, 'readwrite');
      tx.objectStore(IDB_STORE).delete(IDB_KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    db.close();
  } catch {
    // ignore — IDB is best-effort.
  }
}

// --- permission helpers ---------------------------------------------------

type PermState = 'granted' | 'denied' | 'prompt';

/// Boot-safe: NEVER triggers a permission prompt. Used to decide whether
/// to silently auto-load a previously-picked folder on page load.
export async function queryReadPermission(handle: FileSystemDirectoryHandle): Promise<PermState> {
  const fn = (handle as any).queryPermission as
    | ((opts: { mode: string }) => Promise<PermState>) | undefined;
  if (!fn) return 'denied';
  try { return await fn.call(handle, { mode: 'read' }); }
  catch { return 'denied'; }
}

/// Save-time: query first; if `'prompt'`, request. Must be called from a
/// user-gesture handler (Ctrl+S keydown / button click) — browsers reject
/// `requestPermission` outside one.
export async function verifyWritePermission(handle: FileSystemDirectoryHandle): Promise<PermState> {
  const query = (handle as any).queryPermission as
    | ((opts: { mode: string }) => Promise<PermState>) | undefined;
  const request = (handle as any).requestPermission as
    | ((opts: { mode: string }) => Promise<PermState>) | undefined;
  if (!query || !request) return 'denied';
  try {
    const cur = await query.call(handle, { mode: 'readwrite' });
    if (cur === 'granted') return 'granted';
    return await request.call(handle, { mode: 'readwrite' });
  } catch {
    return 'denied';
  }
}
